import React, { useState, useEffect } from 'react';
import { FaTimes, FaEnvelope, FaInfoCircle } from 'react-icons/fa';

import { trackEvent, trackPageView } from '../../services/analytics';
import '../../styles/backgrounds.css';

// フォーム入力コンポーネントの型定義
interface ContactFormProps {
  formData: FormData;
  errors: FormErrors;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onConfirm: () => void;
  onReset: () => void;
  inputBaseClasses: string;
  inputErrorClasses: string;
  inputNormalClasses: string;
  errorTextClasses: string;
}

// 確認画面コンポーネントの型定義
interface ConfirmationScreenProps {
  formData: FormData;
  isSubmitting: boolean;
  submitError: string | null;
  submitSuccess: boolean;
  onBack: () => void;
  onSubmit: () => void;
}

// バリデーション用の定数
const VALIDATION = {
  NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 50,
    REQUIRED_ERROR: '氏名は必須項目です',
    MIN_LENGTH_ERROR: '氏名は2文字以上で入力してください',
    MAX_LENGTH_ERROR: '氏名は50文字以内で入力してください',
  },
  NAME_KANA: {
    MAX_LENGTH: 50,
    PATTERN: /^[ァ-ヶー\s]+$/,
    REQUIRED_ERROR: '氏名カナは必須項目です',
    PATTERN_ERROR: '氏名カナはカタカナで入力してください',
    MAX_LENGTH_ERROR: '氏名カナは50文字以内で入力してください',
  },
  EMAIL: {
    MAX_LENGTH: 100,
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    REQUIRED_ERROR: 'メールアドレスは必須項目です',
    PATTERN_ERROR: '正しいメールアドレスの形式で入力してください',
    MAX_LENGTH_ERROR: 'メールアドレスは100文字以内で入力してください',
  },
  AGE: {
    MIN: 0,
    MAX: 120,
    ERROR: '年齢は0歳から120歳の間で入力してください',
  },
  MESSAGE: {
    MIN_LENGTH: 10,
    MAX_LENGTH: 1000,
    REQUIRED_ERROR: 'お問い合わせ内容は必須項目です',
    MIN_LENGTH_ERROR: 'お問い合わせ内容は10文字以上で入力してください',
    MAX_LENGTH_ERROR: 'お問い合わせ内容は1000文字以内で入力してください',
  },
};

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  nameKana: string;
  email: string;
  age: string;
  message: string;
}

interface FormErrors {
  name?: string;
  nameKana?: string;
  email?: string;
  age?: string;
  message?: string;
}

// API レスポンスの型
interface ApiResponse {
  success: boolean;
  message: string;
}

// フォーム入力コンポーネント
const ContactForm: React.FC<ContactFormProps> = ({
  formData,
  errors,
  onInputChange,
  onConfirm,
  onReset,
  inputBaseClasses,
  inputErrorClasses,
  inputNormalClasses,
  errorTextClasses,
}) => (
  <form className="space-y-4">
    <div>
      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
        氏名 <span className="text-red-500">*</span>
      </label>
      <input
        id="name"
        type="text"
        name="name"
        value={formData.name}
        onChange={onInputChange}
        className={`${inputBaseClasses} ${errors.name ? inputErrorClasses : inputNormalClasses}`}
        required
      />
      {errors.name && <p className={errorTextClasses}>{errors.name}</p>}
    </div>

    <div>
      <label htmlFor="nameKana" className="block text-sm font-medium text-gray-700 mb-1">
        氏名カナ <span className="text-red-500">*</span>
      </label>
      <input
        id="nameKana"
        type="text"
        name="nameKana"
        value={formData.nameKana}
        onChange={onInputChange}
        className={`${inputBaseClasses} ${errors.nameKana ? inputErrorClasses : inputNormalClasses}`}
        placeholder="ヤマダ タロウ"
        required
      />
      {errors.nameKana && <p className={errorTextClasses}>{errors.nameKana}</p>}
    </div>

    <div>
      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
        メールアドレス <span className="text-red-500">*</span>
      </label>
      <input
        id="email"
        type="email"
        name="email"
        value={formData.email}
        onChange={onInputChange}
        className={`${inputBaseClasses} ${errors.email ? inputErrorClasses : inputNormalClasses}`}
        required
      />
      {errors.email && <p className={errorTextClasses}>{errors.email}</p>}
    </div>

    <div>
      <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
        年齢（任意）
      </label>
      <input
        id="age"
        type="number"
        name="age"
        value={formData.age}
        onChange={onInputChange}
        className={`${inputBaseClasses} ${errors.age ? inputErrorClasses : inputNormalClasses}`}
        min="0"
        max="120"
      />
      {errors.age && <p className={errorTextClasses}>{errors.age}</p>}
    </div>

    <div>
      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
        お問い合わせ内容 <span className="text-red-500">*</span>
      </label>
      <textarea
        id="message"
        name="message"
        value={formData.message}
        onChange={onInputChange}
        rows={5}
        className={`${inputBaseClasses} ${errors.message ? inputErrorClasses : inputNormalClasses}`}
        required
      />
      {errors.message && <p className={errorTextClasses}>{errors.message}</p>}
    </div>

    <div className="flex justify-end space-x-3 pt-4">
      <button
        type="button"
        onClick={onReset}
        className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
      >
        リセット
      </button>
      <button
        type="button"
        onClick={onConfirm}
        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
      >
        確認
      </button>
    </div>
  </form>
);

// 確認画面コンポーネント
const ConfirmationScreen: React.FC<ConfirmationScreenProps> = ({
  formData,
  isSubmitting,
  submitError,
  submitSuccess,
  onBack,
  onSubmit,
}) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold mb-4">入力内容をご確認ください</h3>

    <div className="bg-amber-100 p-4 rounded-md space-y-3">
      <div>
        <span className="font-medium text-gray-700">氏名：</span>
        <span>{formData.name}</span>
      </div>
      <div>
        <span className="font-medium text-gray-700">氏名カナ：</span>
        <span>{formData.nameKana}</span>
      </div>
      <div>
        <span className="font-medium text-gray-700">メールアドレス：</span>
        <span>{formData.email}</span>
      </div>
      {formData.age && (
        <div>
          <span className="font-medium text-gray-700">年齢：</span>
          <span>{formData.age}歳</span>
        </div>
      )}
      <div>
        <span className="font-medium text-gray-700">お問い合わせ内容：</span>
        <div className="mt-1 whitespace-pre-wrap">{formData.message}</div>
      </div>
    </div>

    {submitSuccess ? (
      <div className="text-center py-8">
        <div className="text-green-600 text-xl mb-4">お問い合わせを送信しました！</div>
        <p className="text-gray-600">
          ご入力いただいたメールアドレスに確認メールをお送りしました。
          自動的にこの画面は閉じられます。
        </p>
      </div>
    ) : (
      <>
        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-medium">エラーが発生しました</p>
            <p className="text-sm">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            入力に戻る
          </button>
          <button
            type="button"
            onClick={onSubmit}
            className={`px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                送信中...
              </>
            ) : (
              '送信'
            )}
          </button>
        </div>
      </>
    )}
  </div>
);

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      trackPageView('/modal/contact', 'Contact');
    }
  }, [isOpen]);
  // 共通のCSSクラス定数
  const INPUT_BASE_CLASSES = 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2';
  const INPUT_ERROR_CLASSES = 'border-red-500 focus:ring-red-500';
  const INPUT_NORMAL_CLASSES = 'border-gray-300 focus:ring-green-600';
  const ERROR_TEXT_CLASSES = 'mt-1 text-sm text-red-600';

  const [formData, setFormData] = useState<FormData>({
    name: '',
    nameKana: '',
    email: '',
    age: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // バリデーション関数（分割して複雑度を下げる）
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return VALIDATION.NAME.REQUIRED_ERROR;
    if (name.trim().length < VALIDATION.NAME.MIN_LENGTH) return VALIDATION.NAME.MIN_LENGTH_ERROR;
    if (name.trim().length > VALIDATION.NAME.MAX_LENGTH) return VALIDATION.NAME.MAX_LENGTH_ERROR;
    return undefined;
  };

  const validateNameKana = (nameKana: string): string | undefined => {
    if (!nameKana.trim()) return VALIDATION.NAME_KANA.REQUIRED_ERROR;
    if (!VALIDATION.NAME_KANA.PATTERN.test(nameKana.trim()))
      return VALIDATION.NAME_KANA.PATTERN_ERROR;
    if (nameKana.trim().length > VALIDATION.NAME_KANA.MAX_LENGTH)
      return VALIDATION.NAME_KANA.MAX_LENGTH_ERROR;
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return VALIDATION.EMAIL.REQUIRED_ERROR;
    if (!VALIDATION.EMAIL.PATTERN.test(email.trim())) return VALIDATION.EMAIL.PATTERN_ERROR;
    if (email.trim().length > VALIDATION.EMAIL.MAX_LENGTH) return VALIDATION.EMAIL.MAX_LENGTH_ERROR;
    return undefined;
  };

  const validateAge = (age: string): string | undefined => {
    if (!age || !age.trim()) return undefined;
    const ageNum = parseInt(age);
    if (isNaN(ageNum) || ageNum < VALIDATION.AGE.MIN || ageNum > VALIDATION.AGE.MAX) {
      return VALIDATION.AGE.ERROR;
    }
    return undefined;
  };

  const validateMessage = (message: string): string | undefined => {
    if (!message.trim()) return VALIDATION.MESSAGE.REQUIRED_ERROR;
    if (message.trim().length < VALIDATION.MESSAGE.MIN_LENGTH)
      return VALIDATION.MESSAGE.MIN_LENGTH_ERROR;
    if (message.trim().length > VALIDATION.MESSAGE.MAX_LENGTH)
      return VALIDATION.MESSAGE.MAX_LENGTH_ERROR;
    return undefined;
  };

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    const nameKanaError = validateNameKana(formData.nameKana);
    if (nameKanaError) newErrors.nameKana = nameKanaError;

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const ageError = validateAge(formData.age);
    if (ageError) newErrors.age = ageError;

    const messageError = validateMessage(formData.message);
    if (messageError) newErrors.message = messageError;

    return newErrors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // リアルタイムバリデーション（該当フィールドのエラーをクリア）
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      nameKana: '',
      email: '',
      age: '',
      message: '',
    });
    setErrors({});
    setIsConfirming(false);
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(false);
  };

  const handleConfirm = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsConfirming(true);
    }
  };

  // API送信処理を分割
  const getApiUrl = (): string => {
    // お問い合わせ機能はStrapiを使用（Directus移行対象外）
    return 'https://dino-api.interestic.com/api/contact';
  };

  const sendContactRequest = async (apiUrl: string): Promise<ApiResponse> => {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error(`サーバーエラー: ${response.status}`);
    }

    return response.json();
  };

  const handleSubmitSuccess = () => {
    setSubmitSuccess(true);

    // Google Analyticsでお問い合わせ送信を追跡
    trackEvent('contact_form_submit', {
      form_type: 'contact',
      success: true,
    });

    setTimeout(() => {
      handleReset();
      onClose();
    }, 3000);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const apiUrl = getApiUrl();
      const data = await sendContactRequest(apiUrl);

      if (!data.success) {
        throw new Error(data.message || 'お問い合わせの送信に失敗しました。');
      }

      handleSubmitSuccess();
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitError(error instanceof Error ? error.message : '送信中にエラーが発生しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      onKeyDown={e => e.key === 'Escape' && onClose()}
      role="presentation"
      data-testid="modal-overlay"
    >
      <div
        className="rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-labelledby="contact-modal-title"
        aria-modal="true"
        style={{
          fontFamily: "'Noto Sans JP', sans-serif",
        }}
      >
        {/* ヘッダー部分 */}
        <div className="red-earth-bg py-3 px-6 border-b border-amber-800 rounded-t-lg flex justify-between items-center">
          <h2
            id="contact-modal-title"
            className="text-2xl font-bold flex items-center text-amber-100"
          >
            <FaEnvelope className="mr-2" />
            お問い合わせ
          </h2>
          <button
            onClick={handleClose}
            className="text-amber-100 hover:text-white text-2xl"
            aria-label="close"
          >
            <FaTimes />
          </button>
        </div>

        {/* メインコンテンツ部分 */}
        <div
          className="stone-texture-bg overflow-y-auto"
          style={{ maxHeight: 'calc(90vh - 120px)' }}
        >
          <div className="p-6">
            <div className="bg-amber-50 p-6 rounded-lg shadow-md">
              <div className="text-gray-700 leading-relaxed">
                {!isConfirming ? (
                  <ContactForm
                    formData={formData}
                    errors={errors}
                    onInputChange={handleInputChange}
                    onConfirm={handleConfirm}
                    onReset={handleReset}
                    inputBaseClasses={INPUT_BASE_CLASSES}
                    inputErrorClasses={INPUT_ERROR_CLASSES}
                    inputNormalClasses={INPUT_NORMAL_CLASSES}
                    errorTextClasses={ERROR_TEXT_CLASSES}
                  />
                ) : (
                  <ConfirmationScreen
                    formData={formData}
                    isSubmitting={isSubmitting}
                    submitError={submitError}
                    submitSuccess={submitSuccess}
                    onBack={() => setIsConfirming(false)}
                    onSubmit={handleSubmit}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* フッター部分 */}
        <div className="red-earth-bg py-3 px-6 border-t border-amber-800 rounded-b-lg">
          <div className="flex justify-center items-center">
            <div className="text-amber-100 text-sm flex items-center">
              <FaInfoCircle className="mr-1" size={12} />
              <span>個人情報は適切に管理いたします</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
